import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { ConfirmationDialogComponent } from '@app/business-rules/components/confirmation-dialog/confirmation-dialog.component';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DialogService } from '@services/dialog.service';
import { UserService } from '@services/user.service';
import { BaseComponent } from '@shared/components/base-component';
import { View } from '@shared/components/brightview/brightview-types';
import _ from 'lodash';

@Component({
  selector: 'app-data-view-gallery',
  templateUrl: './data-view-gallery.component.html',
  styleUrls: ['./data-view-gallery.component.scss'],
  standalone: false,
})
export class BrightViewGalleryComponent<T> extends BaseComponent {
  private readonly dialogService = inject(DialogService);
  protected readonly userService = inject(UserService);
  protected readonly activeOffcanvas = inject(NgbActiveOffcanvas);
  private readonly cdr = inject(ChangeDetectorRef);

  _views: Array<View.UserView<T>> = [];
  protected pinnedViews: Array<View.UserView<T>>;
  protected defaultViews: Array<View.DefaultView<T>>;
  protected customViews: Array<View.UserView<T>>;
  protected canUnpinViews = signal(true);

  @Input()
  set views(views: Array<View.UserView<T>>) {
    this._views = views;
    this.pinnedViews = this._views.filter(v => v.isPinned);
    this.canUnpinViews.set(this.pinnedViews.length > 1);
    this.defaultViews = this._views.filter(
      v => !v.isCustom && (!('ownerUsername' in v) || v.ownerUsername !== UserService.getCurrent().username),
    );
    this.customViews = this._views.filter(
      v => v.isCustom || ('ownerUsername' in v && v.ownerUsername === UserService.getCurrent().username),
    );
  }
  @Output()
  viewsChange = new EventEmitter<Array<View.UserView<T> | View.DefaultView<T>>>();

  viewTogglePinned(view: View.UserView<T> | View.DefaultView<T>) {
    if (view.isPinned && !this.canUnpinViews()) return;
    view.isPinned = !view.isPinned;
    this.pinnedViews = this._views.filter(v => v.isPinned);
    this.canUnpinViews.set(this.pinnedViews.length > 1);
    this.updateViews();
  }

  viewToggleShared(view: View.UserView<T> | View.DefaultView<T>) {
    if ('isCustom' in view && view.isCustom) {
      view.isCustom = false;
      (view as View.DefaultView<T>).ownerUsername = UserService.getCurrent().username;
    } else {
      (view as View.UserView<T>).isCustom = true;
      if ('ownerUsername' in view) {
        delete view.ownerUsername;
      }
    }
    this.defaultViews = this._views.filter(
      v => !v.isCustom && (!('ownerUsername' in v) || v.ownerUsername !== UserService.getCurrent().username),
    );
    this.customViews = this._views.filter(
      v => v.isCustom || ('ownerUsername' in v && v.ownerUsername === UserService.getCurrent().username),
    );
    this.updateViews();
  }

  viewDropped(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.pinnedViews, event.previousIndex, event.currentIndex);
    this.updateViews();
  }

  viewDeleted(view: View.UserView<T> | View.DefaultView<T>, viewId: number) {
    const confirmationModal = this.dialogService.openModal(ConfirmationDialogComponent, { centered: true });
    confirmationModal.componentInstance.question = `Are you sure you want to delete your ${view.label} view?`;
    this.subscribe(confirmationModal.closed, answer => {
      if (answer === true) {
        this.customViews.splice(viewId, 1);
        this.pinnedViews = this.pinnedViews.filter(v => !_.isEqual(v, view));
        this.updateViews();
        this.cdr.markForCheck();
      }
    });
  }

  private updateViews() {
    this.viewsChange.next([
      ...this.pinnedViews,
      ...this.defaultViews.filter(v => !v.isPinned),
      ...this.customViews.filter(v => !v.isPinned),
    ]);
  }
}
